import { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Divider, ListItem } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { LenderService } from '/src/api';
import { ListCell, ListHeader, ListRow, ViewNotesModal } from '/src/components';
import { useLenderStore } from '/src/context';
import { Lender, Note } from '/src/lib/models';
import { TListLendersRequest, Totals } from '/src/lib/types';
import * as utils from '/src/utils';

export type LendersListProps = {
  /**
   * List of Lenders to display
   */
  data?: Lender[];
  /**
   * Query to include when fetching Lenders
   */
  query?: TListLendersRequest;
};

/**
 * Lenders List
 */
export const LendersList: React.FC<LendersListProps> = observer(({ data, query }) => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = false;
  const rowHeight = 48;

  /** Context **/
  const lenderStore = useLenderStore();

  /** State **/
  const [lenders, setLenders] = useState<Lender[]>([]);
  const [totals, setTotals] = useState<Totals>({});
  const [resolved, setResolved] = useState(false);
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);

  // View Notes popup shows if `notes` is defined
  const [notes, setNotes] = useState<Note[]>();
  const [notesName] = useState<string>();

  /** Computed **/

  const listData = useMemo(() => {
    if (lenders) {
      const list = lenders.toSorted((a, b) => ((a as any)[sortType] > (b as any)[sortType] ? 1 : -1));
      if (sortReverse) list.reverse();

      return list;
    } else return [];
  }, [lenders, sortType, sortReverse]);

  /** Methods **/

  const formatData = (data: Lender[]) => {
    // Prepare totals
    const totals: Totals = {};
    totals.participationPercentage = 0;
    totals.currentBalance = 0;
    totals.feesDue = 0;
    totals.interestDue = 0;

    const lenders = data.map((lender) => {
      lender;

      // Calculate totals
      totals.participationPercentage += lender.participationPercentage;
      totals.currentBalance += lender.principalBalance;
      totals.feesDue += lender.feesDue;
      totals.interestDue += lender.interestDue;

      // Store
      return lender;
    });

    // Save data
    setLenders(lenders);
    setTotals(totals);

    // Set as resolved
    setResolved(true);
  };

  /**
   * Sets sortType and sortReverse based on given sortType
   * @param type string sortType to use
   */
  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    if (data) {
      formatData(data);
    } else if (query && Object.values(query).length) {
      const fetchLenders = async () => {
        const response = await LenderService.query(query);
        const lenders = lenderStore.formatLenderList(response);
        formatData(lenders);
      };

      fetchLenders();
    }
  }, [data, query]);

  /** Render **/
  return (
    <Box>
      {/* HEADER */}
      {!!listData.length && (
        <ListHeader
          items={[
            { propName: 'lenderName', label: 'Investor Name' },
            { propName: 'participationPercentage', label: 'Participation Percentage' },
            { propName: 'principalBalance', label: 'Current Balance' },
            { propName: 'feesDue', label: 'Fees Due' },
            { propName: 'interestDue', label: 'Interest Due' },
          ]}
          sortType={sortType}
          sortReverse={sortReverse}
          sortList={sortList}
        />
      )}

      {!!listData.length && <Divider />}

      {/* LIST */}
      {resolved && !!listData.length && (
        <Box component="md-content" height={listData.length * rowHeight + 'px'} maxHeight={9 * rowHeight + 'px'}>
          {listData.map((lender, i) => (
            <ListRow
              key={lender._id}
              padForScrollbar={listData.length > 8}
              items={[
                { propName: 'lenderName', content: lender.company.name, url: `/investors/${lender.company._id}` },
                {
                  propName: 'participationPercentage',
                  content: utils.percent.format(lender.participationPercentage ?? 0),
                },
                { propName: 'principalBalance', content: utils.currency.format(lender.principalBalance) },
                { propName: 'feesDue', content: utils.currency.format(lender.feesDue ?? 0) },
                {
                  propName: 'interestDue',
                  content: utils.currency.format(lender.interestDue ?? 0),
                },
              ]}
              rowIndex={i}
            />
          ))}
        </Box>
      )}

      <Divider />

      {/* TOTALS */}
      {!!listData.length && (
        <ListItem sx={{ height: rowHeight }}>
          <Box
            display="flex"
            flex="100"
            justifyContent="space-between"
            alignItems="center"
            className={`medium md-subhead-sm text-center ${listData.length > 8 && 'padForScrollbar'}`}
          >
            {[
              { propName: 'lenderName', label: 'Investor Name' },
              { propName: 'participationPercentage', label: 'Participation Percentage', type: 'percent' },
              { propName: 'currentBalance', label: 'Current Balance' },
              { propName: 'feesDue', label: 'Fees Due' },
              { propName: 'interestDue', label: 'Interest Due' },
            ].map(({ propName, type, ...itemProps }, i) => (
              <ListCell
                key={`${propName}-${i}`}
                content={
                  i === 0 ? 'Totals' : utils[type === 'percent' ? 'percent' : 'currency'].format(totals[propName] ?? 0)
                }
                {...itemProps}
              />
            ))}
          </Box>
        </ListItem>
      )}

      {resolved && !listData.length && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <Box component="span">No Lenders found.</Box>
          </Box>
        </ListItem>
      )}

      {!resolved && (
        <ListItem>
          <Box display="flex" flex="100" justifyContent="center" alignItems="center" className="pad-top-bottom">
            <CircularProgress className="md-primary" md-mode="indeterminate" />
          </Box>
        </ListItem>
      )}

      {notes && (
        <ViewNotesModal
          notes={notes}
          name={notesName}
          open={!!notes}
          setOpen={(open) => !open && setNotes(undefined)}
        />
      )}
    </Box>
  );
});
